import { transformChartData } from '../../../../utils/redux';
import {
  addIncidentHelper,
  deleteIncidentHelper,
} from '../../../utils/incidentsHelpers';
import {
  AnalysisStatus,
  IAnalysisInfoFieldsFragment,
  IMeasureDisplayFormatFieldsFragment,
  IReportSettingFieldsFragment,
  Maybe,
} from '../../../../utils/apollo/graphqlSchema';
import { assertNotNil } from '../../../../utils/types';
import { getAnalysisMetadata } from '../../../utils/anlaysisMetadataUtils';
import { current, Draft, PayloadAction } from '@reduxjs/toolkit';
import { IncidentsState } from '../../../reducers/incidents';
import { IIncident } from '../../../../pages/Incidents/interfaces';
import { defaultModalsState, IAnalysesState } from '../types';
import { IAnalysisResponse } from '../../../../utils/apollo/responseTypes';
import {
  getAnalysisSettings,
  toAnalysisInfo,
} from '../../../utils/analysisSettingsUtils';
import { initialChartSettings } from '../../../utils/chartSettings/utils';

export const initialSettings = {
  chartSettings: initialChartSettings,
  chartSettingsSnapshot: initialChartSettings,
  incidents: {},
  chartWidth: 0,
  inspectSubjectId: null,
};

export const populateBuilderInfo = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{ analysisInfo: IAnalysisInfoFieldsFragment }>,
) => {
  const { analysisInfo } = action.payload;
  const builderInfo = toAnalysisInfo(analysisInfo);
  const { id } = analysisInfo;
  if (!draft[id]) {
    // If we didn't have analysis in store before - populate it with
    // default values and set builderInfo
    draft[id] = {
      builderInfo,
      modalsState: defaultModalsState,
      settings: initialSettings,
      chartData: null,
      metadata: null,
      defaultChartSettings: initialChartSettings,
    };
  } else {
    draft[id].builderInfo = builderInfo;
  }
};

export const populateAnalysis = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysis: IAnalysisResponse;
    incidentTypes: IncidentsState['incidentTypes'];
    reportSetting: Maybe<IReportSettingFieldsFragment>;
  }>,
) => {
  const { analysis, incidentTypes, reportSetting } = action.payload;
  const { id, status } = analysis;

  if (status !== AnalysisStatus.Success) {
    draft[id] = {
      builderInfo: toAnalysisInfo(analysis),
      metadata: getAnalysisMetadata(analysis),
      chartData: null,
      settings: initialSettings,
      modalsState: defaultModalsState,
      defaultChartSettings: initialChartSettings,
    };
    return;
  }

  const chartData = transformChartData(analysis);
  const { settings, defaultChartSettings } = getAnalysisSettings({
    analysis,
    incidentTypes,
    reportSetting,
  });
  const metadata = getAnalysisMetadata(analysis);

  draft[id] = {
    builderInfo: toAnalysisInfo(analysis),
    metadata,
    chartData,
    settings,
    defaultChartSettings,
    modalsState: defaultModalsState,
  };
};

export const clearAnalysis = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<string>,
) => {
  const id = action.payload;
  delete draft[id];
};

// ==================== Chart - Line value format related ====================
export const customizeFormat = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    measureKey: string;
    customMeasureFormat: IMeasureDisplayFormatFieldsFragment;
  }>,
) => {
  const { analysisId, measureKey, customMeasureFormat } = action.payload;
  const currDisplayedMeasuresSettings =
    draft[analysisId].settings?.chartSettings.displayedMeasureSettings;

  const selectedMeasure = currDisplayedMeasuresSettings.find(
    (measure: any) => measure.key === measureKey,
  );
  assertNotNil(selectedMeasure);
  selectedMeasure.format = { ...customMeasureFormat };
  return;
};
// ==================== End of Chart - Line value format related ====================
export const handleResizeAnalysis = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    id: string;
    newChartWidth: number;
  }>,
) => {
  const { id, newChartWidth } = action.payload;
  draft[id].settings.chartWidth = newChartWidth;
};

export const renameAnalysis = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    analysisName: string;
    analysisDescription: string;
  }>,
) => {
  const { analysisId, analysisName, analysisDescription } = action.payload;
  const metadata = draft[analysisId].metadata;
  assertNotNil(metadata);
  metadata.display = analysisName;
  metadata.description = analysisDescription;
};

export const fallbackToDefaultState = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<string>,
) => {
  const state = current(draft);
  const analysisId = action.payload;
  if (!state[analysisId]) {
    return;
  }
  // @Kostya: TODO drop?
  // draft[analysisId].current = state[analysisId].default;
};

export const enableEditingForCurrentLoadedAnalyses = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<string[]>,
) => {
  const state = current(draft);
  const analysesIds = action.payload;
  analysesIds.forEach((analysisId: string) => {
    draft[analysisId].settings.chartSettingsSnapshot = {
      ...state[analysisId].settings.chartSettings,
    };
  });
};

export const cancelEditingForCurrentLoadedAnalyses = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<string[]>,
) => {
  const state = current(draft);
  const analysesIds = action.payload;
  analysesIds.forEach((analysisId: string) => {
    draft[analysisId].settings.chartSettings = {
      ...state[analysisId].settings.chartSettingsSnapshot,
    };
    draft[analysisId].settings.chartSettingsSnapshot = initialChartSettings;
  });
};

export const setAnalysisFavorite = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{ analysisId: string; isFavorite: boolean }>,
) => {
  const { analysisId, isFavorite } = action.payload;
  const metadata = draft[analysisId].metadata;
  assertNotNil(metadata);
  metadata.isFavorite = isFavorite;
};

export const toggleSingleTypeSwitch = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{ typeKey: string; analysisId: string }>,
) => {
  const state = current(draft);

  const { typeKey, analysisId } = action.payload;
  const currentState =
    state[analysisId].settings?.chartSettings?.activeIncidentTypes?.[typeKey];
  const allSwitchState =
    state[analysisId].settings?.chartSettings?.activeIncidentTypes?.showAll;
  draft[analysisId].settings.chartSettings.activeIncidentTypes[typeKey] =
    !currentState;

  if (currentState && allSwitchState) {
    draft[analysisId].settings.chartSettings.activeIncidentTypes.showAll =
      !currentState;
    return;
  }
  const activeIncidentTypes =
    state[analysisId].settings?.chartSettings?.activeIncidentTypes;
  const areOtherInactiveSwitches = Object.keys(activeIncidentTypes)
    .filter(
      (activeIncidentTypeKey: string) =>
        activeIncidentTypeKey !== typeKey &&
        activeIncidentTypeKey !== 'showAll',
    )
    .some(
      (activeIncidentTypeKey: string) =>
        !activeIncidentTypes[activeIncidentTypeKey],
    );

  if (!areOtherInactiveSwitches) {
    draft[analysisId].settings.chartSettings.activeIncidentTypes.showAll =
      !currentState;
  }
};

export const toggleAllTypeSwitches = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<string>,
) => {
  const state = current(draft);

  const analysisId = action.payload;
  const currentState =
    state[analysisId].settings?.chartSettings?.activeIncidentTypes?.showAll;
  draft[analysisId].settings.chartSettings.activeIncidentTypes = Object.keys(
    state[analysisId].settings?.chartSettings?.activeIncidentTypes,
  ).reduce((acc: Record<string, boolean>, key: string) => {
    acc[key] = !currentState;
    return acc;
  }, {});
};

export const addNewAnalysisIncident = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    incident: IIncident | null;
    analysisId: string;
    incidentTypes: IncidentsState['incidentTypes'];
  }>,
) => {
  const state = current(draft);

  const { incident, analysisId, incidentTypes } = action.payload;
  assertNotNil(incident);
  const type = incident.type?.id ? incidentTypes?.[incident.type?.id] : null;
  assertNotNil(type);
  const title = incident.name;
  const newIncident = { ...incident, title, type };

  const activeIncidentTypes =
    state[analysisId].settings?.chartSettings?.activeIncidentTypes;
  if (!activeIncidentTypes) {
    return;
  }
  const areOtherInactiveSwitches = Object.keys(activeIncidentTypes)
    .filter(
      (activeEventTypeKey: string) =>
        activeEventTypeKey !== type.id && activeEventTypeKey !== 'showAll',
    )
    .some(
      (activeEventTypeKey: string) => !activeIncidentTypes[activeEventTypeKey],
    );

  if (!areOtherInactiveSwitches) {
    draft[analysisId].settings.chartSettings.activeIncidentTypes.showAll = true;
  }

  draft[analysisId].settings.chartSettings.activeIncidentTypes[type.id] = true;

  addIncidentHelper(draft, newIncident);
};

export const editAnalysisIncident = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    incident: IIncident;
    analysisId: string;
    incidentTypes: IncidentsState['incidentTypes'];
  }>,
) => {
  const { incident, incidentTypes } = action.payload;

  const type = incidentTypes?.[incident?.type?.id];

  const title = incident.name;
  const newIncident = {
    ...incident,
    title,
    type,
  };
  const currentIncidentIndex = deleteIncidentHelper(draft, incident);
  addIncidentHelper(draft, newIncident, currentIncidentIndex);
};

export const deleteAnalysisIncident = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    incident: IIncident;
    analysisId?: string;
  }>,
) => {
  const { incident } = action.payload;
  deleteIncidentHelper(draft, incident);
};

export const toggleIncidentCreateModal = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    modalState: boolean;
    modalData: unknown;
  }>,
) => {
  const { modalState, modalData, analysisId } = action.payload;
  draft[analysisId].modalsState.isCreateIncidentModalOpen = modalState;
  draft[analysisId].modalsState.createIncidentData = modalData;
};

export const updateAliasReplacement = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    aliasId: string;
    replacement: string;
  }>,
) => {
  const { analysisId, aliasId, replacement } = action.payload;
  const chartData = draft[analysisId].chartData;
  assertNotNil(chartData);

  if ('aliasReplacements' in chartData) {
    chartData.aliasReplacements[aliasId] = replacement;
  }
};

export const resetAliasReplacement = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    aliasId: string;
  }>,
) => {
  const { analysisId, aliasId } = action.payload;
  const chartData = draft[analysisId].chartData;
  assertNotNil(chartData);
  if ('aliasReplacements' in chartData) {
    delete chartData.aliasReplacements[aliasId];
  }
};

export const customizeMeasureColor = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    measureKey: string;
    customColor: string;
  }>,
) => {
  const { analysisId, measureKey, customColor } = action.payload;

  const analysis = draft[analysisId];
  assertNotNil(analysis);
  const chartSettings = analysis.settings?.chartSettings;
  const { displayedMeasureSettings } = chartSettings;

  const selectedMeasure = displayedMeasureSettings.find(
    (measure) => measure.key === measureKey,
  );

  if (selectedMeasure) {
    selectedMeasure.color = customColor;
  }
};

export const customizeSliceColor = (
  draft: Draft<IAnalysesState>,
  action: PayloadAction<{
    analysisId: string;
    sliceKey: string;
    customColor: string;
  }>,
) => {
  const { analysisId, sliceKey, customColor } = action.payload;

  const analysis = draft[analysisId];
  assertNotNil(analysis);
  const chartSettings = analysis.settings?.chartSettings;
  const displayedSliceSettings = chartSettings.displayedSliceSettings;
  const slice = displayedSliceSettings.find(
    (sliceSetting) => sliceSetting.key === sliceKey,
  );
  assertNotNil(slice);
  slice.color = customColor;
};
